import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verifica se l'utente è già autenticato al caricamento dell'app
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await authService.getProfile();
          setCurrentUser(response.data);
        } catch (err) {
          console.error('Errore nel recupero del profilo:', err);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password, recaptchaToken) => {
    try {
      setError(null);
      const response = await authService.login(email, password, recaptchaToken);
      const { access, refresh, user, first_login } = response.data;
      
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      
      setCurrentUser({ ...user, first_login });
      return { success: true, first_login };
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il login');
      return { success: false, error: err.response?.data?.detail || 'Errore durante il login' };
    }
  };

  const register = async (userData, recaptchaToken) => {
    try {
      setError(null);
      await authService.register(userData, recaptchaToken);
      return { success: true };
    } catch (err) {
      const data = err.response?.data;
      let message = 'Errore durante la registrazione';
      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.detail) {
          message = data.detail;
        } else {
          const parts = [];
          for (const key of Object.keys(data)) {
            const val = data[key];
            if (Array.isArray(val)) {
              parts.push(`${key}: ${val.join(', ')}`);
            } else if (typeof val === 'string') {
              parts.push(`${key}: ${val}`);
            }
          }
          if (parts.length) message = parts.join(' | ');
        }
      }
      setError(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      setError(null);
      await authService.changePassword(oldPassword, newPassword);
      
      // Se l'utente stava effettuando il primo login, aggiorniamo lo stato
      if (currentUser.first_login) {
        setCurrentUser({ ...currentUser, first_login: false });
      }
      
      return { success: true };
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il cambio password');
      return { success: false, error: err.response?.data?.detail || 'Errore durante il cambio password' };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
